/**
 * Primitivas de UI accesibles (WCAG 2.2 AA, §11.4).
 *
 * Ninguna contiene texto visible: los copys llegan por props desde @thepit/i18n
 * (la guarda `no-hardcoded-string` lo exige).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

/* ------------------------------- Button ---------------------------------- */

type ButtonVariant = 'primary' | 'danger' | 'ghost' | 'accent';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'pit-btn-primary border border-transparent',
  danger: 'pit-btn-danger border border-transparent',
  accent: 'pit-btn-accent border border-transparent',
  ghost: 'pit-btn-ghost text-[var(--pit-text-primary)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  block?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  block = false,
  disabled,
  className = '',
  children,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading}
      className={[
        'pit-touch inline-flex items-center justify-center gap-2 rounded-[var(--pit-radius-md)] px-4 py-2',
        'text-[var(--pit-text-sm)] font-[var(--pit-weight-semibold)] uppercase tracking-[var(--pit-tracking-wide)]',
        'transition-[filter,background-color,border-color,box-shadow,transform] duration-[var(--pit-duration-fast)]',
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        block ? 'w-full' : '',
        BUTTON_STYLES[variant],
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/* -------------------------------- Card ----------------------------------- */

export function Card({
  title,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={['pit-card pit-scanlines relative p-4', className].join(' ')}>
      {(title || actions) && (
        <header className="mb-3 flex items-center justify-between gap-3">
          {title ? (
            <h2 className="pit-mono flex items-center gap-2 text-[var(--pit-text-xs)] uppercase tracking-[var(--pit-tracking-widest)] text-[var(--pit-text-secondary)]">
              <span
                aria-hidden
                className="h-3.5 w-1 rounded-full"
                style={{ background: 'linear-gradient(180deg, var(--pit-accent-red), var(--pit-accent-amber))' }}
              />
              {title}
            </h2>
          ) : (
            <span />
          )}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

/* ------------------------------- Badge ----------------------------------- */

export function Badge({
  children,
  tone = 'neutral',
  pulse = false,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  pulse?: boolean;
}): JSX.Element {
  const tones: Record<string, string> = {
    neutral:
      'border-[var(--pit-border-strong)] text-[var(--pit-text-secondary)] bg-[color-mix(in_srgb,var(--pit-border-strong)_14%,transparent)]',
    success:
      'border-[var(--pit-state-success)] text-[var(--pit-state-success)] bg-[color-mix(in_srgb,var(--pit-state-success)_14%,transparent)]',
    warning:
      'border-[var(--pit-state-warning)] text-[var(--pit-state-warning)] bg-[color-mix(in_srgb,var(--pit-state-warning)_14%,transparent)]',
    danger:
      'border-[var(--pit-state-danger)] text-[var(--pit-state-danger)] bg-[color-mix(in_srgb,var(--pit-state-danger)_14%,transparent)]',
    info: 'border-[var(--pit-state-info)] text-[var(--pit-state-info)] bg-[color-mix(in_srgb,var(--pit-state-info)_14%,transparent)]',
  };
  return (
    <span
      className={[
        'pit-mono inline-flex items-center gap-1 rounded-[var(--pit-radius-sm)] border px-2 py-0.5',
        'text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)]',
        tones[tone],
        pulse ? 'animate-pulse' : '',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/* ------------------------------ Skeleton --------------------------------- */

export function Skeleton({ height = 20, width = '100%' }: { height?: number; width?: string }): JSX.Element {
  return (
    <div
      className="pit-skeleton rounded-[var(--pit-radius-sm)]"
      style={{ height, width }}
      role="status"
      aria-live="polite"
    />
  );
}

/* ----------------------------- EmptyState -------------------------------- */

/** Estado vacío consistente: marca hexagonal + título + ayuda. */
export function EmptyState({
  title,
  hint,
  glyph = '◌',
}: {
  title: string;
  hint?: string;
  glyph?: string;
}): JSX.Element {
  return (
    <div className="pit-anim-up flex flex-col items-center gap-3 py-10 text-center">
      <span
        aria-hidden
        className="pit-hex pit-anim-float grid h-14 w-14 place-items-center"
        style={{
          background:
            'linear-gradient(160deg, color-mix(in srgb, var(--pit-accent-red) 45%, transparent), color-mix(in srgb, var(--pit-accent-violet) 26%, transparent))',
          boxShadow: '0 0 30px -12px var(--pit-accent-red)',
        }}
      >
        <span className="pit-mono text-[var(--pit-text-lg)] font-[var(--pit-weight-black)] text-white">{glyph}</span>
      </span>
      <p className="pit-mono text-[var(--pit-text-sm)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-secondary)]">
        {title}
      </p>
      {hint && <p className="max-w-sm text-[var(--pit-text-xs)] text-[var(--pit-text-muted)]">{hint}</p>}
    </div>
  );
}

/* --------------------------- Barra de tensión ---------------------------- */

export function TensionBar({ value, label }: { value: number; label: string }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-2 w-full overflow-hidden rounded-[var(--pit-radius-full)] bg-[var(--pit-bg-sunken)]"
    >
      <div
        className="h-full bg-[linear-gradient(90deg,var(--pit-corner-red),var(--pit-accent-amber),var(--pit-corner-blue))] transition-[width] duration-[var(--pit-duration-base)]"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/* -------------------------------- Stat ----------------------------------- */

export function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  tone?: 'neutral' | 'red' | 'blue' | 'accent';
}): JSX.Element {
  const tones: Record<string, string> = {
    neutral: 'text-[var(--pit-text-primary)]',
    red: 'text-[var(--pit-corner-red)]',
    blue: 'text-[var(--pit-corner-blue)]',
    accent: 'text-[var(--pit-accent-amber)]',
  };
  return (
    <div className="flex flex-col gap-0.5">
      <span className="pit-mono text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]">
        {label}
      </span>
      <span className={['pit-mono text-[var(--pit-text-lg)] font-[var(--pit-weight-bold)]', tones[tone]].join(' ')}>
        {value}
      </span>
    </div>
  );
}

/* ------------------------------- Dialog ---------------------------------- */

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  closeLabel,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
}): JSX.Element | null {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  // Foco atrapado + Escape (§11.4).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusable = node?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[var(--pit-z-modal)] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="pit-glass pit-anim-up relative w-full max-w-lg overflow-hidden rounded-[var(--pit-radius-lg)] p-5"
      >
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-1"
          style={{
            background:
              'linear-gradient(90deg, var(--pit-corner-red), var(--pit-accent-amber), var(--pit-accent-cyan))',
          }}
        />
        <header className="mb-3 flex items-center justify-between gap-4">
          <h2 id={titleId} className="pit-mono text-[var(--pit-text-lg)] uppercase tracking-[var(--pit-tracking-wide)]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="pit-touch rounded-[var(--pit-radius-sm)] px-2 text-[var(--pit-text-secondary)] hover:text-[var(--pit-text-primary)]"
          >
            ×
          </button>
        </header>
        <div className="mb-4 text-[var(--pit-text-sm)] text-[var(--pit-text-secondary)]">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2">{footer}</footer>}
      </div>
    </div>
  );
}

/* ------------------------------- Toasts ---------------------------------- */

export interface Toast {
  id: string;
  message: string;
  tone: 'success' | 'danger' | 'info';
}

interface ToastApi {
  toasts: Toast[];
  push: (message: string, tone?: Toast['tone']) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: Toast['tone'] = 'info') => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev.slice(-3), { id, message, tone }]);
      window.setTimeout(() => dismiss(id), 6_000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 left-1/2 z-[var(--pit-z-toast)] flex w-[min(92vw,32rem)] -translate-x-1/2 flex-col gap-2 lg:bottom-4"
        style={{ bottom: 'calc(var(--pit-size-tabbar-height, 60px) + 1rem)' }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={[
              'pit-glass pit-anim-up pointer-events-auto flex items-center justify-between gap-3 rounded-[var(--pit-radius-md)] px-4 py-3 text-[var(--pit-text-sm)]',
              toast.tone === 'success' ? 'border-l-2 border-l-[var(--pit-state-success)]' : '',
              toast.tone === 'danger' ? 'border-l-2 border-l-[var(--pit-state-danger)]' : '',
              toast.tone === 'info' ? 'border-l-2 border-l-[var(--pit-state-info)]' : '',
            ].join(' ')}
          >
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="pit-mono text-[var(--pit-text-xs)] text-[var(--pit-text-muted)] hover:text-[var(--pit-text-primary)]"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return ctx;
}
